import { AiToolCallHistoryItem, ChartSpec, ChooseOptionsSpec } from 'types/aiChat';
import type { FigureDefinition } from 'types/aiChatFigure';
import { parseChartSpecFromToolOutput } from './chartSpec';
import { parseChooseOptionsSpec } from './chooseOptionsSpec';
import type { TableFileFormat } from './tableExport';

/** The tool whose output becomes the clickable choices at the end of an assistant turn. */
const CHOOSE_OPTIONS_TOOL = 'chooseOptions';

/**
 * The tool whose output is a Figure's definition: a table, a list, a chart, a brief, a 360 view, a review's findings,
 * a record card or a draft the app draws.
 */
const FIGURE_TOOL = 'showFigure';

const FIGURE_KINDS = new Set(['table', 'list', 'chart', 'brief', 'party360', 'findings', 'record', 'draft']);

/**
 * The chart anchor of an answer stored before Figures, where its chart belongs. Its key is an ordinal —
 * `[[chart:2]]` names the turn's second `chart` call — or a tool call id. Bracket padding is tolerated
 * because the anchor is model output: unmatched, it reaches the reader as text.
 */
const CHART_ANCHOR = String.raw`\[\[chart:[ \t]*([^\]\s]+)[ \t]*\]\]`;

/**
 * A chart specification the model wrote into its own text instead of calling the tool. Rendering it is
 * what keeps a specification from ever reaching the reader as raw JSON; the fence tag is unknown to the
 * syntax highlighter, so it would otherwise be drawn as a plain code block.
 */
// The fallback end is `(?![\s\S])` (absolute end of input), not `$`: under the `m` flag `$` matches at
// every line end, which would truncate a multi-line body at its first newline.
const CHART_FENCE = String.raw`^[ \t]*\x60{3,}[ \t]*chart[ \t]*\r?\n([\s\S]*?)(?:^[ \t]*\x60{3,}[ \t]*$|(?![\s\S]))`;

/**
 * A Figure's anchor: `[[figure:2]]` names the turn's second Figure shown, as the system prompt tells the model, or a
 * stored tool call id. A refused `showFigure` call, which the model makes again, takes no number.
 */
const FIGURE_ANCHOR = String.raw`\[\[figure:[ \t]*([^\]\s]+)[ \t]*\]\]`;

const CHART_TOKEN_PATTERN = `${CHART_ANCHOR}|${FIGURE_ANCHOR}|${CHART_FENCE}`;

const CLOSING_FENCE = /\x60{3,}[ \t]*$/;

/**
 * The marker the model writes above a markdown table the user asked for as a file, naming the format. The table
 * gets a download card for it. Unmatched it would reach the reader as text, so it never stays: one with
 * no table under it is dropped, and so is a second one before the same table. One right after a Figure's anchor
 * is that Figure's (`LEADING_FILE_MARKER`).
 */
const FILE_MARKER = /\[\[file:[ \t]*([^\]\s]*)[ \t]*\]\]/g;

const FILE_FORMATS: Partial<Record<string, TableFileFormat>> = { xlsx: 'xlsx', csv: 'csv' };

// The marker the model writes on the line after a Figure's anchor: the file is built from that Figure's rows.
const LEADING_FILE_MARKER = /^\s*\[\[file:[ \t]*([^\]\s]*)[ \t]*\]\]/;

// Between a marker and its table, blank lines, a heading or another marker; then a header row and its delimiter
// row, the pipes at either end optional, as GFM has them. Each line between has one way to match, or a run of
// blank lines and no table would backtrack exponentially on every streamed token.
const TABLE_AHEAD =
    /^((?:[ \t]*(?:#{1,6}[ \t][^\r\n]*|\[\[file:[^\]\n]*\]\][ \t]*)?\r?\n)*)[^\n]*\|[^\n]*\r?\n[ \t]*\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*(?:\r?\n|$)/;

// The last heading above a marker, with only lines free of pipes after it: the card goes above it, so it stays
// with the table. A table in between, which needs a pipe, keeps the heading to itself.
const HEADING_TAIL = /(?:^|\n)[ \t]*#{1,6}[ \t][^\n]*(?:\n(?![ \t]*#{1,6}[ \t])[^\n|]*)*$/;

// An anchor or a marker arrives over several tokens, so the tail of the text can hold half of one; left in
// place it would flash `[[chart:1` at the reader for a frame before resolving into the chart.
const PARTIAL_ANCHOR_TAIL =
    /\[\[?(?:c|ch|cha|char|chart|chart:[ \t]*[^\]\s]*[ \t]*\]?|f|fi|fil|file|file:[ \t]*[^\]\s]*[ \t]*\]?|fig|figu|figur|figure|figure:[ \t]*[^\]\s]*[ \t]*\]?)?$/;

type TextBlock = { kind: 'text'; text: string; key: string };

export type MessageBlock =
    | TextBlock
    | { kind: 'file'; format: TableFileFormat; key: string }
    | { kind: 'chart'; spec: ChartSpec; key: string }
    | { kind: 'chart-error'; key: string }
    | { kind: 'figure'; definition: FigureDefinition; key: string; fileFormat?: TableFileFormat }
    | { kind: 'options'; spec: ChooseOptionsSpec; key: string }
    | { kind: 'options-error'; key: string };

interface BuildMessageBlocksParams {
    text: string;
    toolCalls: AiToolCallHistoryItem[];
    /** While tokens are still arriving, an unterminated fence is incomplete rather than broken. */
    isStreaming?: boolean;
}

const parseFenceBody = (body: string): ChartSpec | null => {
    try {
        return parseChartSpecFromToolOutput(JSON.parse(body));
    } catch {
        // Half-streamed or malformed JSON. The caller decides between "still arriving" and "broken".
        return null;
    }
};

const parseFigureDefinition = (output: unknown): FigureDefinition | null =>
    typeof output === 'object' && output !== null && FIGURE_KINDS.has(String((output as { kind?: unknown }).kind))
        ? (output as FigureDefinition)
        : null;

/**
 * A text block cut at each file marker that names a table: the text above it, the `file` block, then the
 * table's text from the heading above it on. The marker itself never stays in the text.
 */
const splitAtFileMarkers = ({ text, key }: TextBlock): MessageBlock[] => {
    const blocks: MessageBlock[] = [];
    const claimedTables = new Set<number>();
    let piece = '';
    let pieceKey = key;
    let cursor = 0;

    const pushPiece = () => {
        if (piece.trim()) blocks.push({ kind: 'text', text: piece, key: pieceKey });
    };

    for (const match of text.matchAll(FILE_MARKER)) {
        const end = match.index + match[0].length;
        piece += text.slice(cursor, match.index);
        cursor = end;

        const ahead = TABLE_AHEAD.exec(text.slice(end));
        const tableAt = ahead ? end + ahead[1].length : -1;
        const format = FILE_FORMATS[match[1].toLowerCase()];
        if (!format || !ahead || claimedTables.has(tableAt)) continue;
        claimedTables.add(tableAt);

        const heading = HEADING_TAIL.exec(piece);
        const split = heading ? heading.index + (piece[heading.index] === '\n' ? 1 : 0) : piece.length;
        const rest = piece.slice(split);
        piece = piece.slice(0, split);
        pushPiece();
        blocks.push({ kind: 'file', format, key: `${key}-file-${match.index}` });
        piece = rest;
        pieceKey = `${key}-${match.index}`;
    }

    piece += text.slice(cursor);
    pushPiece();
    return blocks;
};

const buildAnchoredBlocks = ({ text, toolCalls, isStreaming }: BuildMessageBlocksParams): MessageBlock[] => {
    const chartCalls = toolCalls.filter((toolCall) => toolCall.name === 'chart');
    const figureCalls = toolCalls.filter((toolCall) => toolCall.name === FIGURE_TOOL);
    // A running call keeps its number: an anchor written before it settles still finds it.
    const numberedFigureCalls = figureCalls.filter((toolCall) => toolCall.status !== 'error');
    const blocks: MessageBlock[] = [];

    // A model can both call the chart tool and write the same specification into its text. The tool's
    // chart is the one to keep, so a fence is dropped when it repeats any chart the tool produced —
    // whichever of the two the text reaches first. Charts the tool produced are never suppressed: two
    // separate calls are two charts even when their data is identical. parseChartSpec builds its result
    // with a fixed key order, so equal specifications serialise identically.
    const specByCallId = new Map<string, ChartSpec | null>(
        chartCalls.map((toolCall) => [
            toolCall.id,
            toolCall.status === 'success' ? parseChartSpecFromToolOutput(toolCall.output) : null,
        ])
    );
    const toolSpecKeys = new Set([...specByCallId.values()].flatMap((spec) => (spec ? [JSON.stringify(spec)] : [])));
    const consumedToolCallIds = new Set<string>();
    let errorCount = 0;

    // A refused or running call has no definition, so it draws nothing: the model calls again with a fixed one.
    const pushFigure = (toolCall: AiToolCallHistoryItem) => {
        const definition = toolCall.status === 'success' ? parseFigureDefinition(toolCall.output) : null;
        if (definition && !consumedToolCallIds.has(toolCall.id)) {
            consumedToolCallIds.add(toolCall.id);
            blocks.push({ kind: 'figure', definition, key: `figure-${toolCall.id}` });
        }
    };

    // Keyed by source offset: stable while more tokens arrive, since earlier blocks keep their offsets.
    const pushText = (raw: string, start: number) => {
        if (raw.trim()) blocks.push({ kind: 'text', text: raw, key: `text-${start}` });
    };

    const resolveAnchor = (key: string): AiToolCallHistoryItem | undefined =>
        chartCalls.find((candidate) => candidate.id === key) ??
        (/^\d+$/.test(key) ? chartCalls[Number(key) - 1] : undefined);

    const pushChartError = () => {
        errorCount += 1;
        blocks.push({ kind: 'chart-error', key: `chart-error-${errorCount}` });
    };

    // A fresh RegExp per call: a shared global regex carries lastIndex between calls.
    const tokenRegex = new RegExp(CHART_TOKEN_PATTERN, 'gm');
    let cursor = 0;

    for (let match = tokenRegex.exec(text); match !== null; match = tokenRegex.exec(text)) {
        const [matched, anchorId, figureId, fenceBody] = match;

        // A zero-length match cannot advance the cursor and would spin forever.
        if (matched.length === 0) {
            tokenRegex.lastIndex += 1;
            continue;
        }

        pushText(text.slice(cursor, match.index), cursor);
        cursor = match.index + matched.length;

        if (figureId !== undefined) {
            const toolCall =
                figureCalls.find((candidate) => candidate.id === figureId) ??
                (/^\d+$/.test(figureId) ? numberedFigureCalls[Number(figureId) - 1] : undefined);
            if (toolCall) {
                pushFigure(toolCall);
            }
            continue;
        }

        if (anchorId !== undefined) {
            const toolCall = resolveAnchor(anchorId);
            // An anchor is dropped when it resolves to nothing, and when it repeats an anchor already
            // placed: the message's own text is part of the model's context, so it can echo an anchor it
            // has seen, and one chart tool call is one chart. Either way the reader never sees the marker.
            if (!toolCall || consumedToolCallIds.has(toolCall.id)) continue;

            consumedToolCallIds.add(toolCall.id);
            const spec = specByCallId.get(toolCall.id);
            if (spec) {
                blocks.push({ kind: 'chart', spec, key: `chart-${toolCall.id}` });
            } else {
                pushChartError();
            }
            continue;
        }

        const spec = fenceBody !== undefined ? parseFenceBody(fenceBody) : null;
        if (spec) {
            if (!toolSpecKeys.has(JSON.stringify(spec))) {
                blocks.push({ kind: 'chart', spec, key: `chart-fence-${match.index}` });
            }
            continue;
        }

        // Suppress rather than flag an unterminated fence mid-stream: its JSON is simply incomplete.
        if (isStreaming && !CLOSING_FENCE.test(matched.trimEnd())) continue;

        pushChartError();
    }

    const trailingText = text.slice(cursor);
    pushText(isStreaming ? trailingText.replace(PARTIAL_ANCHOR_TAIL, '') : trailingText, cursor);

    // A Figure shows as soon as its definition arrives: after the text so far until its anchor places it.
    figureCalls.forEach(pushFigure);

    // The chart tool returns before the model writes the anchor positioning it, so appending unplaced
    // charts mid-stream would flash each one at the bottom and then move it up as its anchor arrives.
    if (isStreaming) return blocks;

    chartCalls
        .filter((toolCall) => !consumedToolCallIds.has(toolCall.id))
        .forEach((toolCall) => {
            const spec = specByCallId.get(toolCall.id);
            if (spec) {
                blocks.push({ kind: 'chart', spec, key: `chart-${toolCall.id}` });
                return;
            }
            // A chart tool call still running has produced nothing to show yet, and is not a failure.
            if (toolCall.status === 'pending') return;
            pushChartError();
        });

    toolCalls
        .filter((toolCall) => toolCall.name === CHOOSE_OPTIONS_TOOL && toolCall.status === 'success')
        .forEach((toolCall) => {
            const spec = parseChooseOptionsSpec(toolCall.output);
            // The system prompt tells the model not to repeat the question in its text, so the block
            // carries the turn's only question: dropping a rejected one would end the turn on nothing.
            blocks.push(
                spec
                    ? { kind: 'options', spec, key: `options-${toolCall.id}` }
                    : { kind: 'options-error', key: `options-error-${toolCall.id}` }
            );
        });

    return blocks;
};

/**
 * Splits an assistant message into the blocks it should render, in the order the reader should meet them:
 * text, and each Figure and chart at the point in that text where it was produced.
 *
 * A Figure's or a chart's position comes from the anchor the model writes into its text. A Figure no anchor placed
 * is shown after the text, even while the answer streams, and moves to its anchor once that arrives; a chart no
 * anchor placed is shown after the text once the message is complete.
 *
 * Choice blocks have no anchor at all: a turn offering them is the model declining to proceed until it
 * is answered, so they close the message and take the same unanchored path.
 *
 * A chart that cannot be rendered becomes a `chart-error` block rather than disappearing: both the strict
 * client-side parser and the server-side validator reject a malformed specification, and silently dropping
 * either one leaves the reader with no chart and no explanation. Choices the parser rejects become an
 * `options-error` block for the same reason.
 *
 * A file marker right after a Figure's anchor hands its format to that Figure, which builds the file from its rows.
 * Any other file marker becomes a `file` block right before the text block that holds its table.
 */
export const buildMessageBlocks = (params: BuildMessageBlocksParams): MessageBlock[] => {
    const blocks = buildAnchoredBlocks(params);
    return blocks.flatMap((block, index) => {
        if (block.kind === 'figure') {
            const next = blocks[index + 1];
            const marker = next?.kind === 'text' ? LEADING_FILE_MARKER.exec(next.text) : null;
            const fileFormat = marker ? FILE_FORMATS[marker[1].toLowerCase()] : undefined;
            return [fileFormat ? { ...block, fileFormat } : block];
        }
        if (block.kind !== 'text') {
            return [block];
        }
        const text = blocks[index - 1]?.kind === 'figure' ? block.text.replace(LEADING_FILE_MARKER, '') : block.text;
        return splitAtFileMarkers({ ...block, text });
    });
};
